import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { Box, Divider, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { InvestmentsNsureCaseStatus } from "Components/Analyses/GoalsFulfillment/Forms/ProposedSolution/InvestmentsNsure/InvestmentsNsureCaseStatus";
import { Flex } from "Components/_Shared/Core/Flex";
import { Loader } from "Components/_Shared/Loader";
import { useGetAnalysisQuestionnaireEvaluation } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { QuestionnaireStatus } from "Infrastructure/ApiZfp/Schema";
import { QuestionnaireEvaluationSubItemDtoClassEvaluationType as EvaluationType } from "Infrastructure/ApiZfp/Schema";
import type { QuestionnaireEvaluationItemDtoClass } from "Infrastructure/ApiZfp/Schema/questionnaireEvaluationItemDtoClass";
import { useResponsive } from "Infrastructure/Hooks/useResponsive";
import { Radius, Spacing, TABLET_MAX_WIDTH } from "Infrastructure/Themes/Themes";
import { type ResourcePathType, Resources, useResource } from "Infrastructure/Translations/Resources";
import { getCzechPluralForm, inflectMonths, inflectYears } from "Infrastructure/Utils/LanguageUtils";
import { type ReactNode, useContext } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { useLockedInvestmentCaseStatus } from "../_Shared/useLockedInvestmentCaseStatus";
import { useQuestionnaireContext } from "./QuestionnaireContext";
import { QuestionnaireEvaluationSidePanel } from "./QuestionnaireEvaluationSidePanel";
import { QuestionnaireEvaluationVerify } from "./QuestionnaireEvaluationVerify";
import { QuestionnaireGoalCard } from "./QuestionnaireGoalCard";
import { QuestionnaireReEvaluationRequired } from "./QuestionnaireReEvaluationRequired";
import { isReEvaluationRequiredStatus } from "./utils";

const parseLevelValue = (value: string | null): number | null => {
  if (!value) return null;
  const match = value.match(/Level_(\d+)/);
  return match ? Number.parseInt(match[1], 10) : null;
};

const findItem = (items: QuestionnaireEvaluationItemDtoClass[], evaluationType: string) =>
  items.find((item) => item.evaluationType === evaluationType) ?? null;

type ScaleStep = { value: string; label: string };
type TFunc = (resource: ResourcePathType, options?: any) => string;

const levelValueString = (val: string | null): string | null => {
  const n = parseLevelValue(val);
  return n !== null ? String(n) : null;
};

const getTranslation = (options: Record<string, string>, value: string | null | undefined): string | null => {
  if (!value) return null;
  return options[value] ?? null;
};

const getInvestorTypeSteps = (t: TFunc): ScaleStep[] => [
  { value: "WithoutKnowledge", label: t(Resources.Questionnaire.Evaluation.InvestorTypeOptions.None) },
  { value: "Basic", label: t(Resources.Questionnaire.Evaluation.InvestorTypeOptions.Basic) },
  { value: "Informed", label: t(Resources.Questionnaire.Evaluation.InvestorTypeOptions.Informed) },
  { value: "Advanced", label: t(Resources.Questionnaire.Evaluation.InvestorTypeOptions.Advanced) },
];

const getLevelSteps = (firstLabel: string, lastLabel: string): ScaleStep[] =>
  Array.from({ length: 7 }, (_, i) => ({
    value: String(i + 1),
    label: i === 0 ? firstLabel : i === 6 ? lastLabel : "",
  }));

const getLossCapabilityOptions = (t: TFunc): Record<string, string> => ({
  No: t(Resources.Questionnaire.Evaluation.LossCapabilityTypeOptions.No),
  Yes: t(Resources.Questionnaire.Evaluation.LossCapabilityTypeOptions.Yes),
});

const getInvestmentPreferenceOptions = (t: TFunc): Record<string, string> => ({
  WithoutLoss: t(Resources.Questionnaire.Evaluation.InvestmentPreferenceTypeOptions.WithoutLoss),
  Protection: t(Resources.Questionnaire.Evaluation.InvestmentPreferenceTypeOptions.Protection),
  Grow: t(Resources.Questionnaire.Evaluation.InvestmentPreferenceTypeOptions.Grow),
  ProtectionOrGrowth: t(Resources.Questionnaire.Evaluation.InvestmentPreferenceTypeOptions.ProtectionOrGrowth),
  Growth: t(Resources.Questionnaire.Evaluation.InvestmentPreferenceTypeOptions.Growth),
});

const getEsgEUOptions = (t: TFunc): Record<string, string> => ({
  No: t(Resources.Questionnaire.Evaluation.EsgEUTypeOptions.No),
  Yes: t(Resources.Questionnaire.Evaluation.EsgEUTypeOptions.Yes),
});

const getEsgTypeOptions = (t: TFunc): Record<string, string> => ({
  Percent_0: t(Resources.Questionnaire.Evaluation.EsgTypeOptions.Percent_0),
  Percent_1_24: t(Resources.Questionnaire.Evaluation.EsgTypeOptions.Percent_1_24),
  Percent_25_49: t(Resources.Questionnaire.Evaluation.EsgTypeOptions.Percent_25_49),
  Percent_50_100: t(Resources.Questionnaire.Evaluation.EsgTypeOptions.Percent_50_100),
  Percent_100: t(Resources.Questionnaire.Evaluation.EsgTypeOptions.Percent_100),
  NotDefined: t(Resources.Questionnaire.Evaluation.EsgTypeOptions.NotDefined),
  Yes: t(Resources.Questionnaire.Evaluation.EsgTypeOptions.Yes),
  No: t(Resources.Questionnaire.Evaluation.EsgTypeOptions.No),
  YesAdditionally: t(Resources.Questionnaire.Evaluation.EsgTypeOptions.YesAdditionally),
  YesMainly: t(Resources.Questionnaire.Evaluation.EsgTypeOptions.YesMainly),
});

const getEsgConsequenceOptions = (t: TFunc): Record<string, string> => ({
  No: t(Resources.Questionnaire.Evaluation.EsgConsequenceTypeOptions.No),
  Yes: t(Resources.Questionnaire.Evaluation.EsgConsequenceTypeOptions.Yes),
});

type ScaleBarsProps = { steps: ScaleStep[]; activeValue: string | null; isValid: boolean };

const ScaleBars = ({ steps, activeValue, isValid }: ScaleBarsProps) => {
  const activeIndex = steps.findIndex((s) => s.value === activeValue);
  const activeColor = isValid ? "primary.main" : "grey.500";

  return (
    <Flex gap="3px">
      {steps.map((step, i) => (
        <Box key={step.value} flex={1} height={6} borderRadius={Radius.S} bgcolor={i <= activeIndex ? activeColor : "grey.200"} />
      ))}
    </Flex>
  );
};

const EvaluationScale = ({ steps, activeValue, isValid }: ScaleBarsProps) => (
  <Box flex={1} minWidth={0}>
    <ScaleBars steps={steps} activeValue={activeValue} isValid={isValid} />
    <Flex justifyContent="space-between" gap={Spacing.S} mt={0.75}>
      <Typography fontSize={11} color="text.disabled">
        {steps[0]?.label}
      </Typography>
      <Typography fontSize={11} color="text.disabled" textAlign="right">
        {steps[steps.length - 1]?.label}
      </Typography>
    </Flex>
  </Box>
);

const PointsRow = ({ icon, label, points }: { icon: ReactNode; label: string; points: number }) => {
  const { t } = useResource();
  const unit = t(
    getCzechPluralForm(points, {
      one: Resources.Common.Points_one,
      few: Resources.Common.Points_few,
      other: Resources.Common.Points_other,
    }),
  );

  return (
    <Flex justifyContent="space-between" alignItems="center" gap={Spacing.M} py="10px">
      <Flex alignItems="center" gap="6px" color="text.secondary">
        {icon}
        <Typography fontSize={13} color="text.secondary" whiteSpace="nowrap">
          {label}
        </Typography>
      </Flex>
      <Typography fontSize={15} fontWeight={700} whiteSpace="nowrap">
        {points}{" "}
        <Typography component="span" fontSize={13} color="text.secondary">
          {unit}
        </Typography>
      </Typography>
    </Flex>
  );
};

const GoalScale = ({ label, steps, value, isValid }: { label: string; steps: ScaleStep[]; value: string | null; isValid: boolean }) => (
  <Box>
    <Flex justifyContent="space-between" alignItems="baseline" gap={Spacing.S} mb={Spacing.M}>
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
      {value != null && (
        <Typography variant="caption" color="text.secondary" fontWeight={400} noWrap>
          {`${value} / ${steps.length}`}
        </Typography>
      )}
    </Flex>
    <EvaluationScale steps={steps} activeValue={value} isValid={isValid} />
  </Box>
);

const GoalDetail = ({ label, value }: { label: string; value: string }) => (
  <Stack spacing={Spacing.XS} height="100%" pt={Spacing.M} borderTop="1px solid" borderColor="divider">
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontSize={13} fontWeight={500}>
      {value}
    </Typography>
  </Stack>
);

type Props = {
  onReEvaluate: () => void;
  onShowQuestionnaire: () => void;
  onChangeAnswers: () => void;
  status: QuestionnaireStatus;
};

export const QuestionnaireEvaluation = ({ onReEvaluate, onShowQuestionnaire, onChangeAnswers, status }: Props) => {
  const isTablet = useResponsive(TABLET_MAX_WIDTH);
  const { investmentPlans, mode } = useQuestionnaireContext();
  const isPrint = mode === "print";
  const { versionId, analysisId, readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const isReEvaluationRequired = isReEvaluationRequiredStatus(status);
  const isEvaluationValid = isPrint ? true : !isReEvaluationRequired;
  const goalColumnSize = isPrint ? 4 : { xs: 12, sm: 4 };

  const {
    data: evaluationData,
    isPending: isEvaluating,
    refetch: refetchEvaluation,
  } = useGetAnalysisQuestionnaireEvaluation(analysisId, versionId);

  const { lockedCaseStatus, isInvestmentCaseLocked, isInvestmentStatesPending } = useLockedInvestmentCaseStatus(analysisId, versionId);

  if (isEvaluating || isInvestmentStatesPending) {
    return <Loader />;
  }

  const items = evaluationData?.questionnaireEvaluationItems ?? [];
  const investorTypeSteps = getInvestorTypeSteps(t);
  const riskProfileSteps = getLevelSteps(
    t(Resources.Questionnaire.Evaluation.RiskProfileTypeOptions.Level_1),
    t(Resources.Questionnaire.Evaluation.RiskProfileTypeOptions.Level_7),
  );
  const financialSituationSteps = getLevelSteps(
    t(Resources.Questionnaire.Evaluation.FinancialSituationTypeOptions.Level_1),
    t(Resources.Questionnaire.Evaluation.FinancialSituationTypeOptions.Level_7),
  );
  const riskToleranceSteps = getLevelSteps(
    t(Resources.Questionnaire.Evaluation.RiskToleranceTypeOptions.Level_1),
    t(Resources.Questionnaire.Evaluation.RiskToleranceTypeOptions.Level_7),
  );
  const lossCapabilityOptions = getLossCapabilityOptions(t);
  const investmentPreferenceOptions = getInvestmentPreferenceOptions(t);
  const esgEUOptions = getEsgEUOptions(t);
  const esgTypeOptions = getEsgTypeOptions(t);
  const esgConsequenceOptions = getEsgConsequenceOptions(t);

  const globalItems = items.filter((item) => item.investmentPlanExternalID == null);
  const globalInvestorType = findItem(globalItems, EvaluationType.InvestorType);
  const activeInvestorStep = investorTypeSteps.find((s) => s.value === globalInvestorType?.value) ?? null;

  const experiencePoints = Math.round(
    globalInvestorType?.questionnaireEvaluationSubItems?.find((s) => s.evaluationType === EvaluationType.Experience)?.points ?? 0,
  );
  const knowledgePoints = Math.round(
    globalInvestorType?.questionnaireEvaluationSubItems?.find((s) => s.evaluationType === EvaluationType.Knowledge)?.points ?? 0,
  );

  const getStatusContent = () => {
    if (isInvestmentCaseLocked)
      return (
        <Flex pt={Spacing.XXL} pb={Spacing.XXXL} justifyContent={"center"}>
          <InvestmentsNsureCaseStatus caseStatus={lockedCaseStatus} />
        </Flex>
      );

    if (isReEvaluationRequired)
      return (
        <Box pt={Spacing.XXL} pb={Spacing.XXXL}>
          <QuestionnaireReEvaluationRequired status={status} onReEvaluate={onReEvaluate} />
        </Box>
      );

    return (
      <Stack spacing={Spacing.XL} pb={Spacing.L}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600} color="black">
            {t(Resources.Questionnaire.Evaluation.TitleVerification)}
          </Typography>
        </Box>
        <QuestionnaireEvaluationVerify signedAt={evaluationData?.signedAt} onVerified={refetchEvaluation} />
      </Stack>
    );
  };

  return (
    // On A4 the side panel would squeeze the scales, so print always stacks instead of relying on the renderer viewport.
    <Stack direction={isTablet || isPrint ? "column" : "row"}>
      <Stack spacing={Spacing.XL} px={isPrint ? 0 : Spacing.XL} pb={Spacing.XXXL} flex={1} minWidth={0}>
        {!isPrint && getStatusContent()}
        <Stack
          spacing={Spacing.XL}
          py={Spacing.XXL}
          px={7}
          border="1px solid"
          borderRadius={Radius.XL}
          sx={(theme) => ({ bgcolor: theme.colors.brandLighter, borderColor: theme.colors.brandLightBorder, breakInside: "avoid" })}
        >
          <Box>
            <Typography variant="subtitle1" fontSize={20} fontWeight={600} color="black">
              {t(Resources.Questionnaire.Evaluation.Title)}
            </Typography>
            <Typography variant="body2" color="secondary">
              {t(Resources.Questionnaire.Evaluation.Subtitle)}
            </Typography>
          </Box>

          <Box display="grid" gridTemplateColumns={isPrint ? "3fr 1fr" : { xs: "1fr", md: "3fr 1fr" }} columnGap={Spacing.XXL}>
            <Stack spacing={Spacing.M} pr={Spacing.M} minWidth={0}>
              <Typography fontSize={16} fontWeight={700} lineHeight={1.2}>
                <Typography component="span" variant="body2">
                  {`${t(Resources.Questionnaire.Evaluation.InvestorType)}: `}
                </Typography>
                {activeInvestorStep?.label ?? "—"}
              </Typography>
              <ScaleBars steps={investorTypeSteps} activeValue={globalInvestorType?.value ?? null} isValid={isEvaluationValid} />
              <Flex justifyContent="space-between" gap={Spacing.S}>
                <Typography fontSize={12} color="text.secondary">
                  {investorTypeSteps[0].label}
                </Typography>
                <Typography fontSize={12} color="text.secondary" textAlign="right">
                  {investorTypeSteps[investorTypeSteps.length - 1].label}
                </Typography>
              </Flex>
            </Stack>

            <Stack
              display={isPrint ? "flex" : { xs: "none", md: "flex" }}
              justifyContent="center"
              pl={Spacing.XXL}
              borderLeft="1px solid"
              divider={<Divider sx={(theme) => ({ borderColor: theme.colors.brandLightBorder })} />}
              sx={(theme) => ({ borderColor: theme.colors.brandLightBorder })}
            >
              <PointsRow
                icon={<SchoolOutlinedIcon sx={{ fontSize: 16 }} />}
                label={t(Resources.Questionnaire.Evaluation.Knowledge)}
                points={knowledgePoints}
              />
              <PointsRow
                icon={<WorkOutlineOutlinedIcon sx={{ fontSize: 16 }} />}
                label={t(Resources.Questionnaire.Evaluation.Experience)}
                points={experiencePoints}
              />
            </Stack>
          </Box>
        </Stack>

        <Box pt={Spacing.M}>
          <Typography variant="subtitle1" fontSize={17} fontWeight={600} color="black">
            {t(Resources.Questionnaire.Evaluation.GoalsTitle)}
          </Typography>
          <Typography variant="body2" color="secondary">
            {t(Resources.Questionnaire.Evaluation.GoalsSubtitle)}
          </Typography>
        </Box>

        {investmentPlans.map((plan) => {
          const planId = plan.nsureInvestmentPlanExternalId;
          const planItems = items.filter((item) => item.investmentPlanExternalID === planId);

          const riskProfile = findItem(planItems, EvaluationType.RiskProfileType);
          const financialSituation = findItem(planItems, EvaluationType.FinancialSituationType);
          const lossCapability = findItem(planItems, EvaluationType.LossCapabilityType);
          const investmentPreference = findItem(planItems, EvaluationType.InvestmentPreferenceType);
          const esgEU = findItem(planItems, EvaluationType.ESGEUType);
          const riskTolerance = findItem(planItems, EvaluationType.RiskToleranceType);
          const investmentHorizon = findItem(planItems, EvaluationType.InvestmentHorizon);
          const esgType = findItem(planItems, EvaluationType.ESGType);
          const esgConsequence = findItem(planItems, EvaluationType.ESGConsequenceType);

          const horizonMonths = investmentHorizon?.value != null ? Number.parseInt(investmentHorizon.value, 10) : null;
          const horizonYears = horizonMonths != null ? Math.floor(horizonMonths / 12) : null;
          const horizonRemainingMonths = horizonMonths != null ? horizonMonths % 12 : null;
          const horizonText =
            horizonMonths == null
              ? "—"
              : horizonYears! > 0 && horizonRemainingMonths! > 0
                ? `${inflectYears(horizonYears!, t)} ${inflectMonths(horizonRemainingMonths!, t)}`
                : horizonYears! > 0
                  ? inflectYears(horizonYears!, t)
                  : inflectMonths(horizonMonths, t);

          const riskProfileValue = levelValueString(riskProfile?.value ?? null);
          const financialSituationValue = levelValueString(financialSituation?.value ?? null);
          const riskToleranceValue = levelValueString(riskTolerance?.value ?? null);

          const lossCapText = getTranslation(lossCapabilityOptions, lossCapability?.value) ?? lossCapability?.value ?? "—";
          const investPrefText =
            investmentPreference?.text ?? getTranslation(investmentPreferenceOptions, investmentPreference?.value) ?? "—";
          const esgEUText = esgEU?.text ?? getTranslation(esgEUOptions, esgEU?.value) ?? "—";
          const esgTypeText = esgType?.text ?? getTranslation(esgTypeOptions, esgType?.value) ?? "—";
          const esgConseqText = esgConsequence?.text ?? getTranslation(esgConsequenceOptions, esgConsequence?.value) ?? "—";

          const scales = [
            { label: t(Resources.Questionnaire.Evaluation.RiskProfile), steps: riskProfileSteps, value: riskProfileValue },
            {
              label: t(Resources.Questionnaire.Evaluation.FinancialSituation),
              steps: financialSituationSteps,
              value: financialSituationValue,
            },
            { label: t(Resources.Questionnaire.Evaluation.RiskTolerance), steps: riskToleranceSteps, value: riskToleranceValue },
          ];

          const details = [
            { label: t(Resources.Questionnaire.Evaluation.LossCapability), value: lossCapText },
            { label: t(Resources.Questionnaire.Evaluation.InvestmentPreference), value: investPrefText },
            { label: t(Resources.Questionnaire.Evaluation.SustainableInvestmentPreference), value: esgTypeText },
            { label: t(Resources.Questionnaire.Evaluation.EsgEU), value: esgEUText },
            { label: t(Resources.Questionnaire.Evaluation.EsgConseq), value: esgConseqText },
          ];

          return (
            <QuestionnaireGoalCard
              key={planId}
              title={t(Resources.Questionnaire.Horizon[plan.horizon])}
              details={[{ label: t(Resources.Questionnaire.GoalCard.Horizon), value: horizonText }]}
            >
              <Stack spacing={Spacing.XXL}>
                <Grid container spacing={Spacing.XXL}>
                  {scales.map((scale) => (
                    <Grid key={scale.label} size={goalColumnSize}>
                      <GoalScale label={scale.label} steps={scale.steps} value={scale.value} isValid={isEvaluationValid} />
                    </Grid>
                  ))}
                </Grid>
                <Grid container columnSpacing={Spacing.XXL} rowSpacing={Spacing.L}>
                  {details.map((detail) => (
                    <Grid key={detail.label} size={goalColumnSize}>
                      <GoalDetail label={detail.label} value={detail.value} />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </QuestionnaireGoalCard>
          );
        })}
      </Stack>

      <QuestionnaireEvaluationSidePanel
        status={status}
        evaluatedAt={evaluationData?.evaluatedAt}
        signedAt={evaluationData?.signedAt}
        canChangeAnswers={!isReEvaluationRequired}
        isChangeAnswersDisabled={readOnly || isInvestmentCaseLocked}
        onChangeAnswers={onChangeAnswers}
        onShowQuestionnaire={onShowQuestionnaire}
      />
    </Stack>
  );
};
